import { useState } from "react";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCandidateSkills,
  useDeleteProfileSkill,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { ProfileSkillFormModal } from "@/pages/profile/employee/sections/components/ProfileSkillFormModal";
import type { ProfileDetail, ProfileSkillDetail } from "@/interface/profile";

export function SkillsSection({
  profileId,
}: {
  profileId?: number | string;
  profile?: ProfileDetail;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | string | null>(
    null,
  );

  const {
    data: candidateSkillsRes,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateSkills(profileId, { page: 1, page_size: 14 });
  const deleteMutation = useDeleteProfileSkill();

  const skills: ProfileSkillDetail[] =
    candidateSkillsRes?.pages?.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const capitalize = (str?: string) => {
    if (!str) return "Proficient";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getPercent = (level?: string) => {
    const l = (level || "").toLowerCase();
    if (l === "beginner") return 25;
    if (l === "intermediate") return 50;
    if (l === "advanced") return 75;
    if (l === "expert") return 100;
    return 75;
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col">
      <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-primary">Skills & Expertise</h2>
          <p className="text-xs text-slate-500">
            Candidate skills and proficiency ratings
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Skill
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4 max-h-[500px]"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {skills.map((sk: ProfileSkillDetail, i: number) => {
              const skillName = sk.skill_name || "Skill";
              const levelStr = capitalize(sk.proficiency);
              const percent = getPercent(sk.proficiency);

              return (
                <div
                  key={sk.id || i}
                  className="flex items-center gap-4 p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex justify-between mb-2">
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        {skillName}
                      </span>
                      <span className="text-xs text-primary font-semibold">
                        {levelStr}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-1000"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                  {sk.id && (
                    <button
                      onClick={() => setDeleteTargetId(sk.id)}
                      className="text-rose-500 p-2 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}

            {skills.length === 0 && (
              <div className="col-span-full text-center p-8 text-slate-500 text-sm">
                No skills added yet.
              </div>
            )}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more skills...
              </span>
            </div>
          )}
        </div>
      )}

      <ProfileSkillFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        profileId={profileId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Skill"
        description="Are you sure you want to remove this skill?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
